import React, { useState, useEffect } from "react"
import { Typography, Card, Divider } from "@mui/material"

import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { PickersActionBar } from "@mui/x-date-pickers/PickersActionBar"

import dayjs from "dayjs"

import { BarChart } from "@mui/x-charts/BarChart"
import { motion } from "framer-motion"
import { useDemo } from "../../../context/DemoContext"
import DemoCTA from "../../../components/DemoCTA"

import LoadingScreen from "../../../LoadingScreen"
// import { formatCurrency } from "../../../util/formatCurrency"
import { formatCurrency } from "../../../util/formatCurrency"

const currentDayjs = dayjs()

const monthLabels = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
}

function AppealsInfo({}) {
  const { hasDemoAccess, demoData } = useDemo()
  const [showDemoCTA, setShowDemoCTA] = useState(false)

  const [selectedMonth, setSelectedMonth] = useState(null)
  const [selectedYear, setSelectedYear] = useState(currentDayjs)
  const [availableMonths, setAvailableMonths] = useState([])

  const [info, setInfo] = React.useState(null)
  const [infoChart, setInfoChart] = React.useState(null)

  const [appealsMonthly, setAppealsMonthly] = React.useState([])

  useEffect(() => {
    // ---------------------
    // DEMO MODE
    // ---------------------
    if (hasDemoAccess && demoData) {
      setAvailableMonths(demoData.availableMonths || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12])

      setAppealsMonthly(demoData.appealsByMonth)

      const month = selectedMonth?.month() + 1
      const year = selectedYear?.year()

      const match = demoData.appealsInfo?.find((i) => i.month === month && i.year === year) || demoData.appealsInfo?.[0]

      setInfo(match || null)

      if (month) {
        setInfoChart([
          {
            escalation_type: "Rebates",
            totalEscalation: match?.["Escalated Rebates Count"] || 0,
            resolvedEscalation: match?.["Resolved Rebates Count"] || 0,
          },
          {
            escalation_type: "Commission",
            totalEscalation: match?.["Escalated Commission Count"] || 0,
            resolvedEscalation: match?.["Resolved Commission Count"] || 0,
          },
        ])
      } else {
        setInfoChart(null)
      }

      return
    }
  }, [hasDemoAccess, demoData, selectedYear, selectedMonth])

  const handleYearChange = (newYear) => {
    setShowDemoCTA(true)
  }

  const appealsInfoRow = [
    {
      id: "1",
      key: "Total Escalations",
      label: "Total Escalation(s)",
      isCurrency: false,
      classes: "total-text",
    },
    {
      id: "2",
      key: "Escalated Commission",
      label: "Escalated Commission",
      isCurrency: true,
    },

    {
      id: "4",
      key: "Escalated Rebates",
      label: "Escalated Rebates",
      isCurrency: true,
      showExtraDetails: true,
    },

    {
      id: "3",
      key: "Resolved Commission",
      label: "Commissions Recovered",
      classes: "resolved-text",
      isCurrency: true,
    },
    {
      id: "5",
      key: "Resolved Rebates",
      label: "Rebates Recovered",
      classes: "resolved-text",
      isCurrency: true,
      showExtraDetails1: true,
    },
  ]

  const valueFormatter = (value) => {
    return `${value}`
  }

  const chart1Config = {
    xAxis: [
      {
        dataKey: "escalation_type",
        label: "Escalation Type",
        tickLabelStyle: {
          fill: "#1a2bc2",
        },
        labelStyle: {
          fontSize: 12,
          fontWeight: 800,
          fill: "#000", // axis label color (optional)
          fontFamily: "Roboto Mono",
        },
      },
    ],
    yAxis: [
      {
        label: `Total Escalations:\n${selectedMonth?.format("MMMM YYYY")}`,
        width: 80,
        labelStyle: {
          fontSize: 12,
          fontWeight: 800,
          fill: "#000", // axis label color (optional)
          fontFamily: "Roboto Mono",
        },
        tickLabelStyle: {
          fill: "#1a2bc2", // Optional: color
        },
      },
    ],
    series: [
      {
        dataKey: "resolvedEscalation",
        label: "Resolved Escalations",
        // color: "rgb(36 255 156)",
        color: "rgb(11 61 143)",
        color: "rgb(0, 255, 153)",
        color: "rgb(99 228 69)",
        color: "rgb(18 95 0)",

        barThickness: 24,
      },
      {
        dataKey: "totalEscalation",
        label: "Total Escalations",
        color: "#07b3c6",
        color: "rgb(11 61 143)",

        barThickness: 24,
      },
    ],
  }

  const chart2Config = {
    xAxis: [
      {
        dataKey: "month",
        tickLabelStyle: {
          fill: "#000", // Optional: color
        },
        labelStyle: {
          fontSize: 12,
          fontWeight: 600,
          fill: "#000", // axis label color (optional)
          fontFamily: "Roboto Mono",
        },
      },
    ],
    yAxis: [
      {
        label: "Escalations" + ` (${selectedYear?.year() || ""})`,
        width: 60,
        tickLabelStyle: {
          fill: "#000", // Optional: color
        },
        labelStyle: {
          fontSize: 12,
          fontWeight: 600,
          fontFamily: "Roboto Mono",
          fill: "#000", // axis label color (optional)
        },
      },
    ],
    series: [
      {
        dataKey: "ttl",
        label: "Total Escalation",
        valueFormatter,
        // color: "rgba(0,179,147,0.5)",
        // color: "#05004f",
        color: "#07b3c6",
        color: "rgb(11 61 143)",
        // color: "rgb(18 95 0)",
      },
    ],
  }

  const chartSetting = {
    height: 155,
  }

  const chartData = appealsMonthly.map((item) => ({
    ...item,
    month: monthLabels[item.month],
    ttl: item["Total Escalations"],
  }))

  const itemsPerRow = 2
  // console.log("chartData", chartData)

  return (
    <>
      <Card className="visual-appeal-info-main-card visual-info-main-card" sx={{}}>
        {!hasDemoAccess && (
          <LoadingScreen
            h1={"Loading Appeals Info..."}
            style={{
              position: "absolute",
              width: "100%",
              left: 0,
              top: 0,
              backgroundColor: "rgb(0,0,0,0.6)",
              color: "#fff",
              height: "100%",
              zIndex: 99,
              cursor: "default",
            }}
          />
        )}

        <DemoCTA open={showDemoCTA} onClose={() => setShowDemoCTA(false)} />

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "70%",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", width: "100%", gap: 20, flexWrap: "wrap" }}>
              <div style={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom className="va-heading-1">
                  Escalation Summary
                </Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  height: "max-content",
                  marginTop: "5px",
                  flexWrap: "wrap",
                }}
              >
                <DatePicker
                  label="Month"
                  // views={["month"]}
                  views={["month"]}
                  value={selectedMonth}
                  onChange={(newMonth) => setShowDemoCTA(true)}
                  shouldDisableMonth={(date) => {
                    const month = date.month() + 1 // 0-based to 1-based
                    return !availableMonths.includes(month)
                  }}
                  disabled={!availableMonths.length}
                  slotProps={{
                    actionBar: {
                      actions: ["clear", "accept"], // ✅ Adds built-in "Clear" button
                    },
                    textField: {
                      placeholder: "Select month",
                      variant: "outlined",
                      size: "small",
                      InputProps: {
                        disableUnderline: true,
                      },
                      InputLabelProps: {
                        shrink: true,
                      },
                      sx: {
                        background: "var(--va-primary-bg)",
                        width: 150,
                        border: "0px",

                        "& .MuiDateCalendar-root": {
                          paddingTop: "10px",
                        },

                        "& .MuiInputLabel-root": {
                          // fontSize: "0.75rem",
                          color: "var(--va-text-primary-color)",
                          fontWeight: "bold",
                          fontFamily: "Montserrat, san-serif",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                          color: "var(--va-text-primary-color)",
                        },

                        " & .MuiPickersSectionList-section span": {
                          color: "var(--va-text-primary-color)",
                        },
                        // "& .MuiPickersOutlinedInput-notchedOutline": {
                        //   border: "1px solid var(--va-text-primary-color)",
                        // },
                      },
                    },
                  }}
                  slots={{
                    actionBar: PickersActionBar, // ✅ built-in MUI component
                  }}
                />

                <DatePicker
                  label="Year"
                  maxDate={currentDayjs}
                  views={["year"]}
                  value={selectedYear}
                  yearsOrder="desc"
                  onChange={handleYearChange}
                  slotProps={{
                    textField: {
                      placeholder: "Select month",
                      variant: "outlined",
                      size: "small",
                      InputProps: {
                        disableUnderline: true,
                      },
                      InputLabelProps: {
                        shrink: true,
                      },
                      sx: {
                        background: "var(--va-primary-bg)",
                        width: 150,
                        "& .MuiInputLabel-root": {
                          // fontSize: "0.75rem",
                          color: "var(--va-text-primary-color)",
                          fontWeight: "bold",
                          fontFamily: "Montserrat, san-serif",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                          color: "var(--va-text-primary-color)",
                        },

                        " & .MuiPickersSectionList-section span": {
                          color: "var(--va-text-primary-color)",
                        },
                        // "& .MuiPickersOutlinedInput-notchedOutline": {
                        //   border: "1px solid var(--va-text-primary-color)",
                        // },
                      },
                    },
                  }}
                  sx={{
                    minWidth: 150,
                    color: "#cecece",
                  }}
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                // gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)`,
                alignItems: "center",
                // height: "100%",
                gap: "0px",
                flexWrap: "wrap",
              }}
            >
              {info &&
                appealsInfoRow.map((_aiR, index) => (
                  <motion.div
                    onClick={() => setShowDemoCTA(true)}
                    key={_aiR.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="visual-appeal-report-wrapper"
                  >
                    <div className="visual-appeal-report-inner-box">
                      <div className="client-dash-appeal-info-head">{_aiR.label}</div>
                      <div className={`client-dash-appeal-info-text ${_aiR.classes && _aiR.classes}`}>
                        <div>{_aiR.isCurrency && "$"}</div>
                        {_aiR.isCurrency ? formatCurrency(info[_aiR.key]) : info[_aiR.key]}
                        {/*
                        <div>{parseInt(info[_aiR.key] || 0)}</div> */}
                      </div>

                      <div className={`visual-appeal-report-inner-box-right-div ${_aiR.classes && _aiR.classes}`} style={{}} />

                      {_aiR.showExtraDetails && (
                        <div className="hover-content">
                          <p style={{ fontWeight: 600, marginBottom: 6 }}>Rebates Breakdown</p>

                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Financed:</span>
                            <span>${formatCurrency(info["Escalated Rebates For Finance"] || 0)}</span>
                          </div>

                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Normal:</span>
                            <span>${formatCurrency(parseFloat(info[_aiR.key]) - parseFloat(info["Escalated Rebates For Finance"]) || 0)}</span>
                          </div>

                          <Divider sx={{ my: 1 }} />

                          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
                            <span>Total:</span>
                            <span>${formatCurrency(info["Escalated Rebates"] || 0)}</span>
                          </div>
                        </div>
                      )}

                      {_aiR.showExtraDetails1 && (
                        <div className="hover-content">
                          <p style={{ fontWeight: 600, marginBottom: 6 }}>Rebates Breakdown</p>

                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Financed:</span>
                            <span>${formatCurrency(info["Resolved Rebates For Finance"] || 0)}</span>
                          </div>

                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Normal:</span>
                            <span>${formatCurrency(parseFloat(info[_aiR.key]) - parseFloat(info["Resolved Rebates For Finance"]) || 0)}</span>
                          </div>

                          <Divider sx={{ my: 1 }} />

                          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
                            <span>Total:</span>
                            <span>${formatCurrency(info["Resolved Rebates"] || 0)}</span>
                          </div>
                        </div>
                      )}

                      {/* <div className="hover-content">
                        <p>Extra details for {_aiR.label}</p>
                        <p>More info: {info[_aiR.key]}</p>
                      </div> */}
                    </div>
                    {appealsInfoRow.length - 1 != index && (
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          marginLeft: "20px",
                          borderColor: "rgba(255, 255, 255, 0.49)",
                        }}
                        // sx={{
                        //   height: "60px",
                        // }}
                      />
                    )}
                  </motion.div>
                ))}
            </div>
          </div>

          <div className="appeals-info-bar-chart-main-wrapper">
            {infoChart ? <BarChart dataset={infoChart} {...chartSetting} {...chart1Config} /> : <BarChart dataset={chartData} {...chartSetting} {...chart2Config} />}
          </div>
        </div>
      </Card>
    </>
  )
}
export default AppealsInfo
